import React from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import { CheckBold, CloseBig, LongRight, MoreHorizontal } from "@tiny/icons";
import { SmsBuilderStep } from "./useSmsCampaignBuilder";

const STEPS: { key: SmsBuilderStep; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "message", label: "Message" },
];

export interface SmsHeaderProps {
  step: SmsBuilderStep;
  onStepClick: (step: SmsBuilderStep) => void;
  onNext: () => void;
  onDone: () => void;
  /** Next (and the Message pill) stay disabled until a verified sender is picked. */
  nextDisabled?: boolean;
  /** Done stays disabled until the message is valid. */
  doneDisabled?: boolean;
}

function SmsHeader(props: SmsHeaderProps) {
  const { step, onStepClick, onNext, onDone, nextDisabled, doneDisabled } =
    props;
  const isLastStep = step === STEPS[STEPS.length - 1].key;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: 72,
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          flex: "1",
          display: "flex",
          alignItems: "center",
          paddingRight: 5,
        }}
      >
        <Box sx={{ flex: "0 0 54px", padding: "0 12px 0 20px" }}>
          <IconButton size="small" onClick={onDone}>
            <CloseBig />
          </IconButton>
        </Box>
        <Typography
          variant="h4"
          component="h1"
          fontWeight="bold"
          color="grey"
          m={0}
        >
          SMS
        </Typography>
      </Box>

      <Box
        sx={{
          flex: "0 0 360px",
          display: "flex",
          justifyContent: "center",
          gap: 2,
        }}
      >
        {STEPS.map((item, index) => {
          const active = item.key === step;
          const done = STEPS.findIndex((s) => s.key === step) > index;
          const locked = item.key === "message" && nextDisabled;
          const icon = active ? (
            <LongRight sx={{ width: 16, height: 16, mr: 1 }} />
          ) : done ? (
            <CheckBold sx={{ width: 16, height: 16, mr: 1 }} color="primary" />
          ) : (
            <MoreHorizontal sx={{ width: 16, height: 16, mr: 1 }} />
          );
          return (
            <Chip
              key={item.key}
              label={item.label}
              icon={icon}
              clickable={!locked}
              disabled={locked}
              onClick={() => onStepClick(item.key)}
              sx={(theme) => ({
                color: active ? "black" : "grey",
                backgroundColor: active ? "whitesmoke" : "transparent",
                fontWeight: theme.typography.fontWeightMedium,
              })}
            />
          );
        })}
      </Box>

      <Box
        sx={{
          flex: "1",
          display: "flex",
          justifyContent: "flex-end",
          padding: "0 20px",
        }}
      >
        {isLastStep ? (
          <Button variant="contained" onClick={onDone} disabled={doneDisabled}>
            Done
          </Button>
        ) : (
          <Button variant="contained" onClick={onNext} disabled={nextDisabled}>
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default SmsHeader;
